// A phone photo made small enough to upload: jobq-api takes files through Lambda, whose 6 MB request
// limit leaves about 4.4 MB for the file, and a full-size iPhone photo can be bigger. The longest side
// goes to `maxSide` px as a JPEG. The <img> decode applies the photo's EXIF orientation, so the result
// is upright. Anything that can't be shrunk here (not an image, a GIF/SVG, a browser without
// canvas) is returned as it was.
export async function shrinkPhoto(file, { maxSide = 2048, quality = 0.85, keepUnder = 3.5 * 1024 * 1024 } = {}) {
  if (!file || !/^image\//.test(file.type || "") || /gif|svg/.test(file.type)) return file;
  if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") return file;
  const img = document.createElement("img");
  if (typeof img.decode !== "function") return file; // no decoder here: send it as it is
  const src = URL.createObjectURL(file);
  try {
    img.src = src;
    await img.decode();
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return file;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    if (scale === 1 && file.size <= keepUnder) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext && canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return file;
    const name = `${String(file.name || "photo").replace(/\.[^.]+$/, "")}.jpg`;
    return new File([blob], name, { type: "image/jpeg" });
  } catch (e) {
    return file;
  } finally {
    URL.revokeObjectURL(src);
  }
}
